import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Button,
  Card,
  CardBody,
  Label,
  Badge,
  InputGroup,
  InputGroupText,
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import toast from "react-hot-toast";
import OrderServices from "../../../services/OrderingServices/OrderServices";
import { formatDateTime } from "../../../Components/Common/FormatDate";
import CancelModal from "../component/CancelModal";
import Select from "react-select";
import Flatpickr from "react-flatpickr";
import NumberWithCommas from "../../../pages/Pages/Starter/NumberWithCommas";
import ResponsiveDataManagement from "../../../Components/Common/ResponsiveManagementLayout";

// ✅ STEP 1: Import the reusable hook
import useServerSideSearch from "../../../hooks/useServerSideSearch";
import TableSearch from "../../../Components/Common/TableSearch";
import StatusBadge from "../../../Components/Common/Badge";

const OrderList = () => {
  const navigate = useNavigate();
  const today = new Date().toISOString().split("T")[0];

  // Filter states
  const [filters, setFilters] = useState({
    paymentStatus: null,
    orderStatus: null,
    orderReferenceNo: "",
    tableId: null,
    startDate: today,
    endDate: today,
  });

  // ✅ STEP 2: Use the hook with custom filters
  const {
    data: orderList,
    loading,
    totalRows,
    stats: apiStats,
    currentPage,
    pageSize,
    searchQuery,
    handleSearch,
    handleFilterChange,
    handlePaginationChange,
    refetch,
  } = useServerSideSearch(
    OrderServices.getOrderList,
    [],
    500,
    (response) => {
      // Custom response mapper
      const statusMap = {
        Ongoing: "Ongoing",
        Completed: "Completed",
        Cancelled: "Cancelled",
      };

      const paymentMap = {
        Pending: "Pending",
        Paid: "Paid",
        Failed: "Failed",
        Refunded: "Refunded",
        OrderCancelled: "Cancelled",
      };

      const mappedItems = (response.items || []).map((item, index) => ({
        ...item,
        customerName: "",
        customerId: null,
        orderRefrenceNumber: item.orderRefrenceId,
        createdDate: item.createdAt,
        orderStatus: statusMap[item.orderStatus] || item.orderStatus || "Unknown",
        paymentStatus: paymentMap[item.paymentStatus] || item.paymentStatus || "Unknown",
        finalAmount: item.grandTotal,
        tableName: item.tableName || "N/A",
      }));

      return {
        data: mappedItems,
        totalCount: response.totalCount || 0,
        stats: response.summary || {},
      };
    },
    // ✅ Provide initial filters so the first fetch uses today's date range
    {
      fromDate: `${today}T00:00:00`,
      toDate: `${today}T23:59:59`,
    }
  );
  console.log("🚀 ~ OrderList ~ totalRows:", totalRows);

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [tableOptions, setTableOptions] = useState([]);
  const [openDropdownId, setOpenDropdownId] = useState(null);

  // Fetch tables for dropdown
  const fetchAllOrdersForTables = async () => {
    try {
      const response = await OrderServices.getOrderList({
        pageNumber: 1,
        perPage: 1000,
      });

      const tables = [
        ...new Map(
          response.items
            .filter((item) => item.tableId && item.tableName)
            .map((item) => [
              item.tableId,
              { value: item.tableId, label: item.tableName },
            ])
        ).values(),
      ];

      setTableOptions(tables);
    } catch (err) {
      console.error("Failed to fetch tables:", err);
    }
  };

  useEffect(() => {
    fetchAllOrdersForTables();
  }, []);

  // Apply filters to the hook
  useEffect(() => {
    const fromDate = filters.startDate ? `${filters.startDate}T00:00:00` : "";
    const toDate = filters.endDate ? `${filters.endDate}T23:59:59` : "";

    handleFilterChange({
      orderReferenceNo: filters.orderReferenceNo,
      fromDate,
      toDate,
      orderStatus: filters.orderStatus,
      paymentStatus: filters.paymentStatus,
      tableId: filters.tableId,
    });
  }, [filters]);

  const toggleModal = (item) => {
    setIsModalOpen(!isModalOpen);
    setSelectedItem(item);
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      Ordered: "bg-warning",
      Preparing: "bg-secondary",
      Ready: "bg-primary",
      Completed: "bg-success",
      Cancelled: "bg-danger",
      Ongoing: "bg-info",
    };

    return (
      <span className={`badge ${statusStyles[status] || "bg-primary"}`}>
        {status || "No Data"}
      </span>
    );
  };

  const getPaymentBadge = (status) => {
    const statusStyles = {
      Paid: "bg-success",
      Pending: "bg-warning",
      Failed: "bg-danger",
      Processing: "bg-secondary",
      Refunded: "bg-primary",
      PartiallyRefunded: "bg-secondary",
      OrderCancelled: "bg-danger",
      Completed: "bg-success",
      Chargeback: "bg-secondary",
    };
    return (
      <span className={`badge ${statusStyles[status] || "bg-danger"}`}>
        {status || "No Data"}
      </span>
    );
  };

  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleFilterInputChange = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleClearFilters = () => {
    const today = new Date();
    setFilters({
      paymentStatus: null,
      orderStatus: null,
      orderReferenceNo: "",
      tableId: null,
      startDate: formatLocalDate(today),
      endDate: formatLocalDate(today),
    });
  };

  const handleCancel = async () => {
    try {
      const response = await OrderServices.cancelOrder(selectedItem?.id);
      refetch(); // This refreshes the list
      setIsModalOpen(false);
      setSelectedItem(null);
      toast.success(response || "Order cancelled successfully");
    } catch (error) {
      toast.error("Failed to cancel order");
      console.error(error);
    }
  };

  // ✅ STEP 3: Define your columns
  const columns = [
    {
      name: "S.N",
      selector: (row, index) => (currentPage - 1) * pageSize + index + 1,
      sortable: false,
      width: "75px",
    },
    {
      name: "Ref No",
      selector: (row) => row.orderRefrenceNumber,
      cell: (row) => (
        <Link to={`/order/${row.orderRefrenceNumber}`}>
          {row.orderRefrenceNumber}
        </Link>
      ),
      sortable: true,
      width: "14vw",
    },
    {
      name: "Ordered Date",
      selector: (row) => formatDateTime(row.createdDate),
      sortable: true,
      width: "14vw",
    },
    {
      name: "Table",
      selector: (row) => row.tableName || "-",
      sortable: true,
      center: true,
    },
    {
      name: "Status",
      selector: (row) => row.orderStatus,
      // cell: (row) => getStatusBadge(row.orderStatus),
      cell: (row) => (
        <StatusBadge status={row.orderStatus} type="order" pill={true} />
      ),
      sortable: true,
      center: true,
    },
    {
      name: "Payment",
      selector: (row) => row.paymentStatus,
      // cell: (row) => getPaymentBadge(row.paymentStatus),
      cell: (row) => (
        <StatusBadge status={row.paymentStatus} type="payment" pill={true} />
      ),
      sortable: true,
      center: true,
    },
    {
      name: "Amount",
      selector: (row) => row.finalAmount || 0,
      cell: (row) => <NumberWithCommas number={row.finalAmount} />,
      sortable: true,
      right: true,
    },
  ];

  // ✅ STEP 4: Define your stats
  const stats = [
    {
      title: "Total Orders",
      value: apiStats?.totalOrders || 0,
      color: "#0d6efd",
      icon: "bx bx-receipt",
    },
    {
      title: "Total Amount",
      value: <NumberWithCommas number={apiStats?.totalAmount || 0} />,
      color: "#28a745",
      icon: "bx bx-dollar-circle",
    },
    {
      title: "Ongoing Orders",
      value: apiStats?.ongoingOrders || 0,
      color: "#17a2b8",
      icon: "bx bx-loader-circle",
    },
    {
      title: "Cancelled Orders",
      value: apiStats?.totalCancelledOrders || 0,
      color: "#dc3545",
      icon: "bx bx-x-circle",
    },
  ];

  // Count active filters
  const getActiveFilterCount = () => {
    let count = 0;
    if (filters.orderReferenceNo) count++;
    if (filters.tableId) count++;
    if (filters.orderStatus) count++;
    if (filters.paymentStatus) count++;
    return count;
  };

  const activeFilterCount = getActiveFilterCount();

  const orderStatusOption = [
    { value: 1, label: "Ongoing" },
    { value: 2, label: "Completed" },
    { value: 3, label: "Cancelled" },
  ];

  const paymentStatusOption = [
    { value: 2, label: "Paid" },
    { value: 1, label: "Pending" },
  ];

  // ✅ STEP 5: Render
  return (
    <>
      <ResponsiveDataManagement
        title="Orders"
        pageTitle="Order Management"
        data={orderList}
        columns={columns}
        stats={stats}
        searchKeys={[
          "orderRefrenceNumber",
          "tableName",
          "orderStatus",
          "paymentStatus",
        ]}
        loading={loading}
        emptyStateMessage="No orders found"
        // totalCount={totalRows}
        totalRows={totalRows}
        currentPage={currentPage}
        pageSize={pageSize}
        onSearch={handleSearch}
        searchTerm={searchQuery}
        onPaginationChange={handlePaginationChange}
        paginationServer
        paginationTotalRows={totalRows}
        onChangePage={handlePaginationChange}
        onChangeRowsPerPage={(newPerPage, page) =>
          handlePaginationChange({ page, perPage: newPerPage })
        }
        showBreadcrumb={true}
        showSearch={false}
        customActions={[
          {
            label: "Cancel Order",
            icon: "bx bx-x-circle",
            onClick: toggleModal,
            visible: (item) =>
              item.orderStatus !== "Cancelled" &&
              !(
                item.orderStatus === "Completed" &&
                item.paymentStatus === "Paid"
              ),
          },
        ]}
        customHeaderContent={
          <>
            {/* Desktop-only filters */}
            <Row className="d-none d-lg-flex">
              <Col md={4}>
                <h5 className="mb-1 fw-bold text-dark">Orders List</h5>
                {filters.startDate ===
                new Date().toISOString().split("T")[0] ? (
                  <p className="text-muted small mb-0">Today's Orders</p>
                ) : (
                  <p className="text-muted small mb-0">
                    Showing {filters.startDate} to {filters.endDate}
                  </p>
                )}
              </Col>
              <Col md={8}>
                <div className=" d-flex justify-content-between align-content-center align-items-center gap-2 ">
                  {/* Search Bar */}

                  <TableSearch
                    value={searchQuery}
                    onChange={handleSearch}
                    placeholder="Search by all..."
                    style={{ width: "100%" }}
                  />
                  <InputGroup>
                    <Select
                      options={tableOptions}
                      value={
                        tableOptions.find(
                          (option) => option.value === filters.tableId
                        ) || null
                      }
                      onChange={(selectedOption) => {
                        setFilters((prev) => ({
                          ...prev,
                          tableId: selectedOption?.value ?? null,
                        }));
                      }}
                      placeholder="Select by Table"
                      isClearable
                      className="w-100"
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                    />
                  </InputGroup>
                  <InputGroup>
                    <Flatpickr
                      value={
                        filters.startDate && filters.endDate
                          ? `${filters.startDate} to ${filters.endDate}`
                          : ""
                      }
                      options={{
                        mode: "range",
                        dateFormat: "Y-m-d",
                      }}
                      onChange={(dates) => {
                        if (dates.length === 2) {
                          const start = formatLocalDate(dates[0]);
                          const end = formatLocalDate(dates[1]);
                          setFilters((prev) => ({
                            ...prev,
                            startDate: start,
                            endDate: end,
                          }));
                        }
                      }}
                      placeholder="Select Date Range"
                      className="form-control "
                    />
                    <InputGroupText style={{ cursor: "pointer" }}>
                      <i className="bx bx-calendar"></i>
                    </InputGroupText>
                  </InputGroup>
                </div>
              </Col>
            </Row>
          </>
        }
        mobileCardRenderer={(item, index) => (
          <>
            {/* Only show title once, on first item */}
            {index === 0 && (
              <div className="mb-3 d-flex justify-content-between align-items-center">
                {/* Left side */}
                <div>
                  <h5 className="fw-bold text-dark fs-4">Orders List</h5>
                  <p className="text-muted small mb-0">
                    {filters.startDate === today
                      ? "Today's Orders"
                      : `${filters.startDate} to ${filters.endDate}`}
                  </p>
                </div>

                {/* Right side */}
                <Button
                  color="primary"
                  outline
                  size="sm"
                  onClick={() => setFilterModalOpen(true)}
                  className="position-relative"
                >
                  <i className="bx bx-filter-alt" />
                  {activeFilterCount > 0 && (
                    <Badge
                      color="danger"
                      pill
                      className="position-absolute top-0 end-0 translate-middle-x"
                    >
                      {activeFilterCount}
                    </Badge>
                  )}
                </Button>
              </div>
            )}

            <Card
              key={item.id || index}
              className="shadow-sm rounded-3 border-0 border-start border-4 border-primary mb-3"
              style={{
                cursor: "pointer",
                borderLeft: "4px solid #5B71B9",
                transition: "all 0.2s",
              }}
              onClick={() => navigate(`/order/${item.orderRefrenceNumber}`)}
            >
              <CardBody className="py-3">
                <div className="d-flex justify-content-between">
                  <div>
                    <h6 className="fw-bold text-primary mb-1">
                      {item.orderRefrenceNumber}
                    </h6>
                    <p className="small text-muted mb-1">
                      {formatDateTime(item.createdDate)} •{" "}
                      {item.tableName || "Takeaway"}
                    </p>
                    <h6 className="fw-bold text-success mb-2">
                      Rs. <NumberWithCommas number={item.finalAmount} />
                    </h6>
                    <div className="d-flex gap-2">
                      {getStatusBadge(item.orderStatus)}
                      {getPaymentBadge(item.paymentStatus)}
                    </div>
                  </div>

                  {/* More Options Dropdown */}
                  {item.orderStatus !== "Cancelled" &&
                    !(
                      item.orderStatus === "Completed" &&
                      item.paymentStatus === "Paid"
                    ) && (
                      <Dropdown
                        isOpen={openDropdownId === item.id}
                        toggle={() =>
                          setOpenDropdownId(
                            openDropdownId === item.id ? null : item.id
                          )
                        }
                      >
                        <DropdownToggle
                          tag="div"
                          className="text-muted"
                          onClick={(e) => e.stopPropagation()}
                          style={{ cursor: "pointer" }}
                        >
                          <i className="ri-more-2-fill fs-3" />
                        </DropdownToggle>

                        <DropdownMenu end onClick={(e) => e.stopPropagation()}>
                          <DropdownItem
                            className="text-danger"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedItem(item);
                              setIsModalOpen(true);
                              setOpenDropdownId(null);
                            }}
                          >
                            <i className="bx bx-x-circle me-2" />
                            Cancel Order
                          </DropdownItem>
                        </DropdownMenu>
                      </Dropdown>
                    )}
                </div>
              </CardBody>
            </Card>
          </>
        )}
      />

      {/* Mobile Filter Offcanvas */}
      <Offcanvas
        isOpen={filterModalOpen}
        toggle={() => setFilterModalOpen(!filterModalOpen)}
        direction="bottom"
        backdrop={true}
        scrollable
        style={{
          height: "85vh", // Takes most of the screen
          maxHeight: "90vh",
          borderTopLeftRadius: "24px",
          borderTopRightRadius: "24px",
        }}
      >
        <OffcanvasHeader
          toggle={() => setFilterModalOpen(!filterModalOpen)}
          className="border-bottom bg-light"
          style={{ borderTopLeftRadius: "24px", borderTopRightRadius: "24px" }}
        >
          <div className="d-flex align-items-center gap-2">
            <i className="bx bx-filter-alt fs-5"></i>
            <span className="fw-bold">Filter Orders</span>
            {activeFilterCount > 0 && (
              <Badge color="primary" pill>
                {activeFilterCount} Active
              </Badge>
            )}
          </div>
        </OffcanvasHeader>

        <OffcanvasBody className="d-flex flex-column p-3">
          <div className="flex-grow-1">
            <div className="mb-4">
              <Label className="fw-bold mb-2">
                <i className="bx bx-search me-1"></i>Search
              </Label>
              <TableSearch
                value={searchQuery}
                onChange={handleSearch}
                placeholder="Search by all..."
                style={{ width: "100%" }}
              />
            </div>

            {/* Date Range */}
            <div className="mb-4">
              <Label className="fw-bold mb-2">
                <i className="bx bx-calendar me-1"></i>Date Range
              </Label>
              <Flatpickr
                value={
                  filters.startDate && filters.endDate
                    ? `${filters.startDate} to ${filters.endDate}`
                    : ""
                }
                options={{
                  mode: "range",
                  dateFormat: "Y-m-d",
                }}
                onChange={(dates) => {
                  if (dates.length === 2) {
                    const start = formatLocalDate(dates[0]);
                    const end = formatLocalDate(dates[1]);
                    setFilters((prev) => ({
                      ...prev,
                      startDate: start,
                      endDate: end,
                    }));
                  }
                }}
                placeholder="Select Date Range"
                className="form-control"
              />
            </div>

            {/* Table */}
            <div className="mb-4">
              <Label className="fw-bold mb-2">
                <i className="bx bx-table me-1"></i>Table
              </Label>
              <Select
                options={tableOptions}
                value={
                  tableOptions.find(
                    (option) => option.value === filters.tableId
                  ) || null
                }
                onChange={(selectedOption) => {
                  handleFilterInputChange(
                    "tableId",
                    selectedOption?.value ?? null
                  );
                }}
                placeholder="Select Table"
                isClearable
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />
            </div>

            {/* Order Status */}
            <div className="mb-4">
              <Label className="fw-bold mb-2">
                <i className="bx bx-package me-1"></i>Order Status
              </Label>
              <Select
                options={orderStatusOption}
                value={orderStatusOption.find(
                  (option) => option.value === filters.orderStatus
                )}
                onChange={(selectedOption) => {
                  handleFilterInputChange(
                    "orderStatus",
                    selectedOption?.value ?? null
                  );
                }}
                placeholder="Select Order Status"
                isClearable
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />
            </div>

            {/* Payment Status */}
            <div className="mb-4">
              <Label className="fw-bold mb-2">
                <i className="bx bx-credit-card me-1"></i>Payment Status
              </Label>
              <Select
                options={paymentStatusOption}
                value={paymentStatusOption.find(
                  (option) => option.value === filters.paymentStatus
                )}
                onChange={(selectedOption) => {
                  handleFilterInputChange(
                    "paymentStatus",
                    selectedOption?.value ?? null
                  );
                }}
                placeholder="Select Payment Status"
                isClearable
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />
            </div>
          </div>

          {/* Footer Buttons */}
          <div
            className="bg-white border-top pt-3 d-flex gap-2"
            style={{ marginTop: "auto" }}
          >
            <Button
              color="light"
              className="w-50 border shadow-sm"
              onClick={handleClearFilters}
            >
              <i className="bx bx-x me-1"></i>Clear All
            </Button>

            <Button
              color="primary"
              className="w-50 shadow-sm"
              onClick={() => setFilterModalOpen(false)}
            >
              <i className="bx bx-check me-1"></i>Apply Filters
            </Button>
          </div>
        </OffcanvasBody>
      </Offcanvas>
      <CancelModal
        isOpen={isModalOpen}
        toggle={() => {
          setIsModalOpen(false);
          setSelectedItem(null);
        }}
        onDeleteClick={handleCancel}
      />
    </>
  );
};

export default OrderList;
